import { tooltipLoader } from 'components/conceptLink/TooltipLoader';
import { IRefIds } from 'constants/refs';
import * as lodash from 'lodash';
import { del, get, post, put } from 'services/request';
import { IItemId, IPosition } from 'types/content';
import { BaseModel, IModelCourseOptions, IModuleSearch } from './BaseModel';
import { IIllustration } from '../types/illustration';
import { IAsyncTask } from '../types/asyncTask';
import { IAudioDetails } from '../types/audio';

export type IVoc = {
  id: number;
  title: string;
  wordClass: string;
  notesStatus: number;
  position: IPosition;
  illustration: boolean;
} & {
  [prop: string]: string;
};

export type IVocEdit = {
  id: number;
  title: string;
  wordClass: number;
  vocCategories: IAsyncTask<number[]>;
  level: number | null; // course level of the concept: 0 free use · 1–6 A1.1…C1+ · 9 out of courses · null unassigned
  levelTask?: IAsyncTask<number | null>;
  deletable: boolean;
  wordClassEditable: boolean;
  dialects: IVocDialect[];
  compounds: number[];
  tree: IVocTreeEdit;
  parent: number | null;
  parentSynonym: number | null;
  genderBase: number | null;
  hasGenderDerived: boolean;
  illustration: IIllustration;
};

export type IVocTreeEdit = {
  id: number;
  title: string;
  current: boolean;
  gender: 'm' | 'f' | null;
  genderPair: { id: number; title: string } | null;
  children: IVocTreeEdit[];
  synonyms: IVocTreeEdit[];
};

export type IVocDialect = {
  id: number;
  vocId: number;
  dialectCode: string;
  dialectId: number;
  langId: number;
  revision: boolean;
  source: string;
  target: string;
  targetWithArticle: string;
  gender: string;
  transcription: string;
  refLevel: number;
  nInflects: IAsyncTask<number>;
  audio: IAudioDetails;
  sourceEditable: boolean;
  sourceShaded: boolean;
  targetShaded: boolean;
  inflectShaded: boolean;
  genderShaded: boolean;
  audioShaded: boolean;
  targetEnabled: boolean;
  targetCanBeDisabled: boolean;
  targetHidden: boolean;
  genderHidden: boolean;
  transcriptionHidden: boolean;
  refLevelHidden: boolean;
  audioHidden: boolean;
  inflectHidden: boolean;
  wordClass: number;
};

type IVocSearch = IModuleSearch & {
  dialect?: string;
  catId?: number;
};

export default class VocabularyModel extends BaseModel<IVoc, IVocEdit> {
  constructor(options: IModelCourseOptions) {
    super(options);
    this.ref = IRefIds.vocs;
    this.gridName = 'Vocs';
    this.getGridColumns();
  }

  async getGridSearch(text: string, dialect?: string) {
    await super.getGrid();

    if (this.gridColumns.length === 0) {
      await this.getGridColumns();
    }
    if (!dialect) {
      dialect = 'title';
    }

    this.gridData = await get<IVoc[]>(`courses/${this.courseId}/vocs?dialect=${dialect}&text=${text}`);

    this.loadingGrid = false;
    this.render();
  }

  async getGridSearchCatId(catId: number) {
    await super.getGrid();

    if (this.gridColumns.length === 0) {
      await this.getGridColumns();
    }

    this.gridData = await get<IVoc[]>(`courses/${this.courseId}/vocs?category=${catId}`);

    this.loadingGrid = false;
    this.render();
  }

  createGridColumnsEndpoint() {
    return `grid-columns/${this.gridName}?course=${this.courseId}`;
  }

  async getGridRow(itemId: number) {
    return await get<IVoc>(`courses/vocs/${itemId}`);
  }

  async new() {
    await super.new();

    if (this.courseId === 0) {
      const [item, dialects] = await Promise.all([
        get<IVocEdit>(`vocs/new`),
        get<IVocDialect[]>(`courses/${this.courseId}/voc-dialects/new`),
      ]);
      item.dialects = dialects;
      await this.setItem(item);
    } else {
      const [item, sources, targets] = await Promise.all([
        get<IVocEdit>(`vocs/new`),
        get<IVocDialect[]>(`courses/${this.courseId}/vocs-target-dialects/new`),
        get<IVocDialect[]>(`courses/${this.courseId}/vocs-source-dialects/new`),
      ]);
      item.dialects = [...targets, ...sources];
      await this.setItem(item);
    }
    this.render();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated as IVocEdit;
    if (id === this.NEW_CREATE_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }

    this.invalidateTooltip();
  }

  async updateItem() {
    const itemToSave = this.itemUpdated as IVocEdit;
    const itemDialects = this.getItemValue('dialects');
    const { id } = itemToSave;
    let dialectsSave = itemToSave.dialects;
    if (itemToSave.wordClass === this.getItemValue('wordClass')) {
      //update only dialects changed
      dialectsSave = dialectsSave.filter(
        (dialect: IVocDialect, index: number) => JSON.stringify(dialect) !== JSON.stringify(itemDialects[index]),
      );
    }

    // Extract vocCategories value from IAsyncTask wrapper
    const itemToSaveWithCategories = {
      ...itemToSave,
      vocCategories: itemToSave.vocCategories.value
    };

    await put(`vocs/${id}`, lodash.omit(itemToSaveWithCategories, ['dialects']));

    await Promise.all(
      dialectsSave.map((dialect: IVocDialect) => put(`vocs/${id}/dialects/${dialect.dialectId}`, dialect)),
    );
    await Promise.all([this.updateGridRow(id, 'update'), this.loadItemEdit(id)]);
  }

  async updateItemParent(parent: number | null, onError?: (message: string) => void) {
    await super.save();
    const itemToSave = this.itemUpdated as IVocEdit;
    const result = await put<{result?: number; error?: string}>(`/vocs/${itemToSave.id}/parent`, {
      parent: parent ?? 0,
    });

    if (result.data && result.data.error) {
      if (onError) {
        onError(result.data.error);
      } else {
        alert(`Error: ${result.data.error}`);
      }
      this.loadingItem = false;
      this.render();
      return;
    }

    await this.loadItemEdit(itemToSave.id);
  }

  async updateVocParent(vocId: number, parent: number, onError?: (message: string) => void) {
    await super.save();
    const result = await put<{result?: number; error?: string}>(`/vocs/${vocId}/parent`, {
      parent,
    });

    if (result.data && result.data.error) {
      if (onError) {
        onError(result.data.error);
      } else {
        alert(`Error: ${result.data.error}`);
      }
      this.loadingItem = false;
      this.render();
      return;
    }

    const itemToSave = this.itemUpdated as IVocEdit;
    await this.loadItemEdit(itemToSave.id);
  }

  async updateItemParentSynonym(synonym: number | null, onError?: (message: string) => void) {
    await super.save();
    const itemToSave = this.itemUpdated as IVocEdit;
    const result = await put<{result?: number; error?: string}>(`/vocs/${itemToSave.id}/parent-synonym`, {
      parentSynonym: synonym ?? 0,
    });

    if (result.data && result.data.error) {
      if (onError) {
        onError(result.data.error);
      } else {
        alert(`Error: ${result.data.error}`);
      }
      this.loadingItem = false;
      this.render();
      return;
    }

    await this.loadItemEdit(itemToSave.id);
  }

  async updateVocParentSynonym(vocId: number, synonym: number, onError?: (message: string) => void) {
    await super.save();
    const result = await put<{result?: number; error?: string}>(`/vocs/${vocId}/parent-synonym`, {
      parentSynonym: synonym,
    });

    if (result.data && result.data.error) {
      if (onError) {
        onError(result.data.error);
      } else {
        alert(`Error: ${result.data.error}`);
      }
      this.loadingItem = false;
      this.render();
      return;
    }

    const itemToSave = this.itemUpdated as IVocEdit;
    await this.loadItemEdit(itemToSave.id);
  }

  async updateItemGenderBase(genderBase: number | null, onError?: (message: string) => void) {
    await super.save();
    const itemToSave = this.itemUpdated as IVocEdit;
    const result = await put<{ result?: number; error?: string }>(`/vocs/${itemToSave.id}/gender-base`, {
      genderBase: genderBase ?? 0,
    });

    if (result.data && result.data.error) {
      if (onError) {
        onError(result.data.error);
      } else {
        alert(`Error: ${result.data.error}`);
      }
      this.loadingItem = false;
      this.render();
      return;
    }

    await this.loadItemEdit(itemToSave.id);
  }

  async updateVocGenderBase(vocId: number, genderBase: number, onError?: (message: string) => void) {
    await super.save();
    const result = await put<{ result?: number; error?: string }>(`/vocs/${vocId}/gender-base`, {
      genderBase,
    });

    if (result.data && result.data.error) {
      if (onError) {
        onError(result.data.error);
      } else {
        alert(`Error: ${result.data.error}`);
      }
      this.loadingItem = false;
      this.render();
      return;
    }

    const itemToSave = this.itemUpdated as IVocEdit;
    await this.loadItemEdit(itemToSave.id);
  }

  /**
   * Check if vocabulary categories are read-only
   * Categories are read-only when the vocabulary is a synonym child
   * (has a parent synonym set), because categories are inherited from the parent
   */
  isVocCategoriesReadonly(): boolean {
    const item = this.itemUpdated as IVocEdit;
    return item?.parentSynonym !== null && item?.parentSynonym !== undefined;
  }

  async createItem() {
    const itemToCreate = this.itemUpdated as IVocEdit;

    // Extract vocCategories value from IAsyncTask wrapper
    const itemToCreateWithCategories = {
      ...itemToCreate,
      vocCategories: itemToCreate.vocCategories.value,
    };

    const result = await post<IItemId>(`vocs`, lodash.omit(itemToCreateWithCategories, ['dialects']));

    if (result.ok && result.data) {
      const newItemId = result.data.id;

      await Promise.all(
        itemToCreate.dialects.map((dialect: IVocDialect) =>
          put(`vocs/${newItemId}/dialects/${dialect.dialectId}`, dialect),
        ),
      );

      // Finalize vocabulary creation (triggers async tasks like category assignment)
      await this.finalizeCreation(newItemId);

      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async finalizeCreation(vocId: number): Promise<void> {
    await post(`/vocs/${vocId}/finalize-creation`, {});
  }

  async delete() {
    const { id } = this.itemUpdated as IVocEdit;

    const result = await del(`vocs/${id}`);

    if (result.ok) {
      await Promise.all([this.updateGridRow(id, 'remove'), this.new()]);
      this.invalidateTooltip();
    } else {
      console.error(JSON.stringify(result.data));
    }
  }

  async loadItemData(itemId: number): Promise<IVocEdit> {
    if (this.courseId === 0) {
      const [item, dialects] = await Promise.all([
        get<IVocEdit>(`vocs/${itemId}`, { courseId: this.courseId }),
        get<IVocDialect[]>(`courses/${this.courseId}/voc-dialects/${itemId}`),
      ]);

      item.dialects = dialects;
      return item;
    } else {
      const [item, targets, sources] = await Promise.all([
        get<IVocEdit>(`vocs/${itemId}`, { courseId: this.courseId }),
        get<IVocDialect[]>(`courses/${this.courseId}/vocs-target-dialects/${itemId}`),
        get<IVocDialect[]>(`courses/${this.courseId}/vocs-source-dialects/${itemId}`),
      ]);

      item.dialects = [...targets, ...sources];
      return item;
    }
  }

  async search(data: IVocSearch) {
    if (typeof data.catId !== 'undefined') {
      await this.getGridSearchCatId(data.catId);
    } else {
      const text = data.text ? data.text : '';
      if (text !== '') {
        await this.getGridSearch(text, data.dialect);
      }
    }
  }

  private invalidateTooltip() {
    tooltipLoader && tooltipLoader.invalidate({ ref: IRefIds.vocs, courseId: this.courseId, id: this.itemId });
  }

  async newVocAI(wordClass: number, description: string, examples: string) {
    this.loadingItem = true;
    this.render();
    const newVoc = await get<IVocEdit>(`/vocs/ai-new`, {
      wordClass,
      description,
      examples,
    });
    console.log(newVoc);
    await this.setItem(newVoc);
    this.render();
  }

  async recalculateLevel(): Promise<void> {
    const vocId = this.getItemId();
    if (vocId <= 0) return;

    this.loadingItem = true;
    this.render();

    // clears the level and enqueues the level task
    await post(`/vocs/${vocId}/assign-level`, {});

    await this.loadItemEdit(vocId);
  }

  async recreateCategories(): Promise<void> {
    const vocId = this.getItemId();
    if (vocId <= 0) return;

    // Show loading state for better UX
    this.loadingItem = true;
    this.render();

    // Trigger the async task
    await post(`/vocs/${vocId}/assign-categories`, {});

    // Reload the vocabulary to show the async task
    await this.loadItemEdit(vocId);
  }
}
